/**
 * Se o usuário pode usar a área do Gestor, e até quando.
 *
 * O acesso é a soma de duas fontes, de propósito separadas:
 *
 *  - `profiles.subscription_status`, espelho fiel do Stripe (só o servidor
 *    grava — migration 0057);
 *  - `liberacoes_acesso`, a liberação provisória enquanto o boleto não
 *    compensa (P-46): desbloqueio em confiança do cliente ou liberação manual
 *    do superadmin.
 *
 * A liberação nunca altera o status da assinatura. Se alterasse, o espelho
 * deixaria de dizer o que o Stripe diz, e o fim do prazo exigiria alguém para
 * "desfazer" — aqui ele simplesmente passa.
 *
 * Isto é experiência de uso, não fronteira de segurança: quem decide o que o
 * usuário lê e grava é a RLS.
 */

import { supabase } from '@/lib/supabase';

export interface AcessoDaConta {
  liberado: boolean;
  /** Fim da liberação provisória, em ms. `null` quando o acesso vem da assinatura. */
  ate: number | null;
}

const STATUS_ATIVOS = ['active', 'trialing'];

export async function acessoDaConta(userId: string): Promise<AcessoDaConta> {
  const { data: perfil, error } = await supabase
    .from('profiles')
    .select('subscription_status, account_id')
    .eq('id', userId)
    .maybeSingle();

  if (error || !perfil) return { liberado: false, ate: null };
  if (STATUS_ATIVOS.includes(perfil.subscription_status ?? '')) {
    return { liberado: true, ate: null };
  }
  if (!perfil.account_id) return { liberado: false, ate: null };

  const { data: liberacao } = await supabase
    .from('liberacoes_acesso')
    .select('expira_em')
    .eq('account_id', perfil.account_id)
    .is('deletado_em', null)
    .gt('expira_em', new Date().toISOString())
    .order('expira_em', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!liberacao?.expira_em) return { liberado: false, ate: null };
  return { liberado: true, ate: new Date(liberacao.expira_em).getTime() };
}
